import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from 'oidc-client-ts';
import authService from '../services/authService';
import { accountSessionManager } from '../business/auth/AccountSessionManager';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (tenantName?: string, forcePrompt?: boolean, returnUrl?: string) => Promise<void>;
  loginWithGoogle: (tenantName?: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // A switched-account session lives on the target account's origin, where there is no OIDC user at
  // all — the person never logged in on that subdomain. Without this the app would bounce a
  // perfectly valid switched session to /login.
  const [hasAccountSession, setHasAccountSession] = useState<boolean>(() => accountSessionManager.isActive());

  useEffect(() => {
    // Load user from storage on mount
    const loadUser = async () => {
      try {
        //console.log('Loading user...');
        const currentUser = await authService.getUser();
        //console.log('Loaded user:', currentUser);

        // If the person has since logged in natively to the account they had switched into, the
        // switched session is redundant — drop it so the stale token stops outranking the real one.
        const nativeTenantId = currentUser?.access_token
          ? authService.getTenantIdFromToken(currentUser.access_token)
          : null;
        if (currentUser && !currentUser.expired) {
          accountSessionManager.reconcileWithNativeSession(nativeTenantId);
        }
        setHasAccountSession(accountSessionManager.isActive());

        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();

    // Set up event listeners for user changes only if userManager exists
    const userManager = authService.getUserManager();
    
    if (userManager) {
      const handleUserLoaded = (loadedUser: User) => {
        setUser(loadedUser);
      };

      const handleUserUnloaded = () => {
        // During signoutRedirect, oidc-client-ts removes the user from storage
        // which fires this event. If we set user=null here, React re-renders and
        // redirects to /login before the browser can navigate to /connect/logout.
        // Skip the state update during active logout — the page is navigating away.
        if (authService.isLoggingOut) return;
        setUser(null);
      };

      const handleUserSignedOut = () => {
        setUser(null);
      };

      userManager.events.addUserLoaded(handleUserLoaded);
      userManager.events.addUserUnloaded(handleUserUnloaded);
      userManager.events.addUserSignedOut(handleUserSignedOut);

      return () => {
        userManager.events.removeUserLoaded(handleUserLoaded);
        userManager.events.removeUserUnloaded(handleUserUnloaded);
        userManager.events.removeUserSignedOut(handleUserSignedOut);
      };
    }
  }, []);

  const login = async (tenantName?: string, forcePrompt?: boolean, returnUrl?: string) => {
    await authService.login(tenantName, forcePrompt, returnUrl);
  };

  const loginWithGoogle = async (tenantName?: string) => {
    await authService.loginWithGoogle(tenantName);
  };

  const logout = async () => {
    // Clear the switched session first: logout navigates away, and leaving it behind would let the
    // next visit to this origin resume an account the person just signed out of.
    accountSessionManager.clear();
    setHasAccountSession(false);
    await authService.logout();
  };

  const value = {
    user,
    isAuthenticated: (!!user && !user.expired) || hasAccountSession,
    isLoading,
    login,
    loginWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};